// =========================================================================================@@
// Last Updated Date: Feb 18, 2023
// Last Updated By: Steven Yuen
// Status Level: 3
// ===========================================================================================

import React from 'react'
import { Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { LockGlyph12Icon } from 'assets/icons'
import InitiativeDefaultThumbnail from 'src/sites/kits/Initiative/components/DefaultThumbnail'
import { Title } from 'src/sites/kits/UI'

const EventListItemRow = ({
   borderStyle,
   coverMedia,
   name,
   numAttendees,
   linkTo,
   location,
   pastEvent,
   parentInitiativeName,
   parentInitiativePrivacy,
   privacy,
   startDate,
   startTime,
   textColor,
   virtual
}) => {
   const hasParentInitiative = parentInitiativeName && parentInitiativePrivacy

   // We want to use the more restrictive privacy between the event and its parent (if it has one)
   const hasRestrictedParentInitiative = hasParentInitiative &&
      ['admin', 'draft', 'private'].includes(parentInitiativePrivacy)

   const applicablePrivacy = hasRestrictedParentInitiative && privacy === 'inherit'
      ? parentInitiativePrivacy
      : privacy

   return (
      <Link to={linkTo}>
         <View
            display="flex"
            alignItems="center"
            padding="24px 0px"
            borderTop={borderStyle}>
            <View
               flex="0 0 auto"
               width="48px"
               height="48px"
               position="relative"
               backgroundColor="#eee"
               backgroundImage={coverMedia && `url(${coverMedia?.file.thumbnailUrlW480})`}
               backgroundSize="cover"
               backgroundPosition="bottom center"
               backgroundRepeat="no-repeat"
               borderRadius="6px"
               marginRight="24px">
               {!coverMedia && (
                  <InitiativeDefaultThumbnail
                     highlightColor="transparent"
                     iconColor="#ccc"
                     iconName="event"
                     iconSize="16px"
                  />
               )}
            </View>
            <View display="flex" flex="1 1 auto" flexFlow="column">
               <View flex="1 1 auto" marginBottom="4px">
                  <Title size="sm">
                     {name}
                  </Title>
               </View>
               <View className="ui-meta-text" display="flex" alignItems="center" flex="0 0 auto">
                  {['admin', 'draft', 'private'].includes(applicablePrivacy) && (
                     <>
                        {applicablePrivacy === 'private' && (
                           <View
                              position="relative"
                              top="-2px"
                              marginRight="5px"
                              opacity="0.5">
                              <LockGlyph12Icon width="7px" height="7px" color={textColor} />
                           </View>
                        )}
                        <View flex="0 0 auto">
                           <Text
                              size="1.5"
                              color={applicablePrivacy === 'private' ? textColor : '#ba723f'} transform="capitalize">
                              {applicablePrivacy !== 'admin' && applicablePrivacy}
                              {applicablePrivacy === 'admin' && 'Admin-only'}
                           </Text>
                        </View>
                        {((virtual || location || startDate) && (
                           <View className="ui-meta-text" padding="0px 4px">
                              &#183;
                           </View>
                        ))}
                     </>
                  )}
                  <View flex="0 0 auto">
                     <Text size="1.5" color={virtual ? '#eb8044' : textColor}>
                        {virtual && 'Online'}
                        {!virtual && (location.city || location.state) && `${location.city}${location.city && location.state && ', '}${location.state}`}
                     </Text>
                  </View>
                  {(virtual || location.city || location.state) && startDate && (
                     <View className="ui-meta-text" padding="0px 4px">
                        &#183;
                     </View>
                  )}
                  <View flex="0 0 auto">
                     {startDate}
                     {startTime && ` at ${startTime}`}
                  </View>
               </View>
            </View>
            {numAttendees > 5 && (
               <View className="ui-meta-text" flex="0 0 auto" display="none[a-b] block[c-f]">
                  {`${numAttendees} people ${pastEvent ? 'attended' : 'attending'}`}
               </View>
            )}
         </View>
      </Link>
   )
}

EventListItemRow.propTypes = {
   borderStyle: PropTypes.string,
   coverMedia: PropTypes.object,
   name: PropTypes.string.isRequired,
   linkTo: PropTypes.string.isRequired,
   location: PropTypes.object,
   numAttendees: PropTypes.number.isRequired,
   parentInitiativeName: PropTypes.string,
   parentInitiativePrivacy: PropTypes.oneOf(['admin', 'draft', 'private', 'inherit']),
   pastEvent: PropTypes.bool,
   privacy: PropTypes.string.isRequired,
   startDate: PropTypes.string,
   startTime: PropTypes.string,
   textColor: PropTypes.string,
   virtual: PropTypes.bool.isRequired
}

EventListItemRow.defaultProps = {
   borderStyle: '1px solid var(--primaryLineColor)',
   coverMedia: undefined,
   location: undefined,
   parentInitiativeName: undefined,
   parentInitiativePrivacy: undefined,
   pastEvent: false,
   startDate: undefined,
   startTime: undefined,
   textColor: 'var(--titleTextColor)'
}

export default EventListItemRow
